import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import EntityModal from '../../../components/common/tab-modal/TabModal';
import { useToastService } from '../../../components/toast/ToastService';
import { useAppDispatch, useAppSelector } from '../../../hooks/storeHooks';
import {
  CompletionData,
  FluidData,
  GLCompletionData,
  InstallationData,
  PumpData,
  RunlifeData,
} from '../../../models/CompletionModels';
import {
  createCombinedCompletion,
  editCombinedCompletion,
  fetchCombinedCompletion,
  fetchPumpManufacturerSeries,
  fetchPumpManufacturerTypes,
} from '../../../services/well-completions/WellCompletionsService';
import { RootState } from '../../../store';
import {
  parseApiDate,
  validateAllTabs,
  validateTabFields,
  mapApiErrorsToFieldValidation,
} from '../../../utilities/completionsUtils';
import Loader from '../../common/Loader/Loader';
import { getCompletionTabsByLiftType } from './CompletionTabs/CompletionDropdownData';
import CompletionsBoresTab from './CompletionTabs/CompletionsBoresTab';
import CompletionsCompletionTab from './CompletionTabs/CompletionsCompletionTab';
import { EditableTableRef } from './CompletionTabs/CompletionsEditableTable';
import CompletionsFluidTab from './CompletionTabs/CompletionsFluidTab';
import CompletionsGLCompletionsTab from './CompletionTabs/CompletionsGLCompletionsTab';
import CompletionsInstallationTab from './CompletionTabs/CompletionsInstallationTab';
import CompletionsPumpsTab from './CompletionTabs/CompletionsPumpsTab';
import CompletionsRunlifeTab from './CompletionTabs/CompletionsRunlifeTab';
import CompletionsValvesTab, { fixValvePayload } from './CompletionTabs/CompletionsValvesTab';
import { TAB_VALIDATION_CONFIGS } from '../../../utilities/completionsUtils';
import { formatDateWithUtcOffset } from '../../../utilities/DateUtils';

interface AddCompletionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  onSave?: () => void;
  isEditMode?: boolean;
}

const initialInstallationState: InstallationData = {
  liftType: '2',
  manufacturer: '',
  manufacturerController: '',
  installNumber: null,
  status: '',
  blockUpdates: false,
  excludeFromReports: false,
  purchasedSystem: false,
  usedSystem: false,
};

const initialRunlifeState: RunlifeData = {
  installDate: null,
  startDate: null,
  failDate: null,
  pullDate: null,
  teardownDate: null,
  runlifeDays: 0,
};

const initialFluidState: FluidData = {
  oilGravity: null,
  gasGravity: null,
  reservoirPressure: null,
  reservoirTemperature: '',
  inflowModel: '',
};

const initialGLCompletionState: GLCompletionData = {
  reservoirPressure: null,
  reservoirTemperature: null,
  gasGravity: null,
  gasGravityInjected: null,
  oilGravity: null,
  solutionGasOilRatio: '',
  formationVolumeFactorCorrelation: '',
  viscosityCorrelation: '',
  inflowModel: '',
  ohtc: null,
  gaugeDepth: null,
};

const initialCompletionState: CompletionData = {
  OHTCDefault: null,
  PumpMeasured: null,
  PumpTrueVertical: null,
  BottomholeMeasured: null,
  BottomholeTrueVertical: null,
  GeometryTubingOD: null,
  GeometryTubingID: null,
  GeometryTubingRoughness: null,
  GeometryCasingOD: null,
  GeometryCasingID: null,
  GeometryCasingRoughness: null,
  MotorOD: null,
  MotorHp60Hz: null,
  MotorVolts60Hz: null,
  MotorCurrents60Hz: null,
  VsdNameplateAmps: null,
  VsdNameplateKva: null,
  MotorToPumpSpeedRatio: null,
  GeometryPumpIntakeRestriction: null,
  ShaftLimitHp60Hz: null,
  CableNo: null,
  CableRoundFlat: null,
};

const initialPumpState: PumpData = {
  pumpUpperManufacturer: '',
  pumpUpperSeries: '',
  pumpUpperId: null,
  pumpUpperType: '',
  pumpUpperTVD: 0,
  pumpMidManufacturer: '',
  pumpMidSeries: '',
  pumpMidId: null,
  pumpMidType: '',
  pumpMidTVD: null,
  pumpLowerManufacturer: '',
  pumpLowerSeries: '',
  pumpLowerId: null,
  pumpLowerType: '',
  pumpLowerTVD: null,
  liquidSeparationEfficiency: null,
  gasSeparationEfficiency: null,
  gasSeparator: null,
  co2Flood: null,
  permanentMagnetMotor: null,
  subcool: null,
};

// GL lift types that use GLCompletion
const GL_TYPES = new Set(['3', '8', '9']);

const CreateCompletionsModal: React.FC<AddCompletionsModalProps> = ({
  isOpen,
  onClose,
  title,
  onSave,
  isEditMode = false,
}) => {
  const dispatch = useAppDispatch();
  const { showSuccessToast, showErrorToast } = useToastService();

  const selectedLiftType = useAppSelector((state: any) => state.CreateCompletions.selectedLiftType);
  const wellId = useAppSelector((state: RootState) => state.WellTestIdSlice?.data?.WellId);
  const combinedCompletionsData = useAppSelector((state: any) => state.CreateCompletions.combinedCompletions.data);
  const combinedCompletionsLoading = useAppSelector(
    (state: any) => state.CreateCompletions.combinedCompletions.loading,
  );
  const createCompletionLoading = useAppSelector((state: any) => state.CreateCompletions.createCompletion.loading);
  const createCompletionError = useAppSelector((state: any) => state.CreateCompletions.createCompletion.error);
  const editCompletionError = useAppSelector((state: any) => state.CreateCompletions.editCompletion.error);
  const fluidCompletionsData = useAppSelector((state: any) => state.CreateCompletions.combinedCompletions.data?.Fluid);
  const espCompletionData = useAppSelector(
    (state: any) => state.CreateCompletions.combinedCompletions.data?.ESPCompletion,
  );
  const glCompletionData = useAppSelector(
    (state: any) => state.CreateCompletions.combinedCompletions.data?.GLCompletion,
  );
  const glBoreCompletionData = useAppSelector(
    (state: any) => state.CreateCompletions.combinedCompletions.data?.GLCompletion?.Bores,
  );
  const glValveCompletionData = useAppSelector(
    (state: any) => state.CreateCompletions.combinedCompletions.data?.GLCompletion?.Valves,
  );
  const utcOffset = useAppSelector((state: any) => state.assetTimezone.data?.utcOffset || 0);
  const manufacturersWithControllers = useAppSelector(
    (state: any) => state.CreateCompletions.manufacturersWithControllers.data,
  );

  const [activeTab, setActiveTab] = useState('installation');
  const [installation, setInstallation] = useState<InstallationData>(initialInstallationState);
  const [runlife, setRunlife] = useState<RunlifeData>(initialRunlifeState);
  const [fluid, setFluid] = useState<FluidData>(initialFluidState);
  const [pumpData, setPumpData] = useState<PumpData>(initialPumpState);
  const [glCompletion, setGLCompletion] = useState<GLCompletionData>(initialGLCompletionState);
  const [completionData, setCompletionData] = useState<CompletionData>(initialCompletionState);
  const [boresData, setBoresData] = useState<any[]>(glBoreCompletionData || []);
  const [valvesData, setValvesData] = useState<any[]>(glValveCompletionData || []);
  const [tabErrors, setTabErrors] = useState<{ [tabName: string]: string[] }>({});
  const [fieldValidationErrors, setFieldValidationErrors] = useState<{
    [tabName: string]: { [fieldName: string]: string };
  }>({});

  const installationTabRef = useRef<any>();
  const runlifeTabRef = useRef<any>();
  const fluidTabRef = useRef<any>();
  const glCompletionTabRef = useRef<any>();
  const completionTabRef = useRef<any>();
  const boresTabRef = useRef<EditableTableRef>(null);
  const valvesTabRef = useRef<EditableTableRef>(null);
  const pumpTabRef = useRef<any>();

  useEffect(() => {
    if (isEditMode && installation.liftType && wellId) {
      const installationId = combinedCompletionsData?.Id || 0;
      dispatch(
        fetchCombinedCompletion({
          wellId,
          installationId,
          originalMop: combinedCompletionsData?.MethodOfProduction,
          newMop: Number(installation.liftType),
        }),
      );
    }
  }, [installation.liftType, dispatch]);

  const determineLiftType = useMemo(() => {
    if (installation.liftType && installation.liftType !== '') {
      return String(installation.liftType);
    }
    if (
      combinedCompletionsData?.MethodOfProduction !== undefined &&
      combinedCompletionsData?.MethodOfProduction !== null
    ) {
      return String(combinedCompletionsData.MethodOfProduction);
    }
    if (combinedCompletionsData?.LiftTypeValue !== undefined && combinedCompletionsData?.LiftTypeValue !== null) {
      return String(combinedCompletionsData.LiftTypeValue);
    }
    if (selectedLiftType) return selectedLiftType;
    return '';
  }, [
    installation.liftType,
    selectedLiftType,
    combinedCompletionsData?.MethodOfProduction,
    combinedCompletionsData?.LiftTypeValue,
  ]);

  const selectedTabs = useMemo(() => {
    return getCompletionTabsByLiftType(determineLiftType);
  }, [determineLiftType]);

  const enhancedTabs = useMemo(
    () =>
      selectedTabs.map((tab) => ({
        ...tab,
        hasError: tabErrors[tab.id]?.length > 0,
      })),
    [selectedTabs, tabErrors],
  );

  const completions = useAppSelector((state: any) => state.WellCompletions.currentCompletion.data || []);
  const validateTabs = useCallback(() => {
    const formData = { installation, runlife, fluid, glCompletion, pumpData, completionData };
    const errors = validateAllTabs(formData, determineLiftType, completions, isEditMode);
    setTabErrors(errors);

    const fieldErrors: { [tabName: string]: { [fieldName: string]: string } } = {
      installation: validateTabFields('installation', installation),
      runlife: validateTabFields('runlife', runlife),
    };

    if (GL_TYPES.has(determineLiftType)) {
      fieldErrors.glCompletion = validateTabFields('glCompletion', glCompletion);
    } else if (determineLiftType === '2') {
      fieldErrors.fluid = validateTabFields('fluid', fluid);
      fieldErrors.pumps = validateTabFields('pumps', pumpData);
      fieldErrors.completion = validateTabFields('completion', completionData);
    }

    setFieldValidationErrors(fieldErrors);

    const triggerValidations = () => {
      installationTabRef.current?.triggerValidation?.(installation);
      runlifeTabRef.current?.triggerValidation?.(runlife);

      if (determineLiftType === '2') {
        fluidTabRef.current?.triggerValidation?.(fluid);
        pumpTabRef.current?.triggerValidation?.(pumpData);
        completionTabRef.current?.triggerValidation?.(completionData);
      }
      if (GL_TYPES.has(determineLiftType)) {
        glCompletionTabRef.current?.triggerValidation?.(glCompletion);
      }
    };
    triggerValidations();

    return Object.values(errors).every((errorArray) => errorArray.length === 0);
  }, [
    installation,
    runlife,
    fluid,
    glCompletion,
    determineLiftType,
    pumpData,
    completionData,
    completions,
    isEditMode,
  ]);

  useEffect(() => {
    if (espCompletionData?.InstalledPump1?.Manufacturer) {
      dispatch(
        fetchPumpManufacturerSeries({
          field: 'pumpUpperManufacturer',
          manufacturer: espCompletionData.InstalledPump1.Manufacturer,
        }),
      );

      if (espCompletionData?.InstalledPump1?.Series) {
        dispatch(
          fetchPumpManufacturerTypes({
            field: 'pumpUpperSeries',
            manufacturer: espCompletionData.InstalledPump1.Manufacturer,
            series: espCompletionData.InstalledPump1.Series,
          }),
        );
      }
    }
    if (espCompletionData?.InstalledPump2?.Manufacturer) {
      dispatch(
        fetchPumpManufacturerSeries({
          field: 'pumpMidManufacturer',
          manufacturer: espCompletionData.InstalledPump2.Manufacturer,
        }),
      );
      if (espCompletionData?.InstalledPump2?.Series) {
        dispatch(
          fetchPumpManufacturerTypes({
            field: 'pumpMidSeries',
            manufacturer: espCompletionData.InstalledPump2.Manufacturer,
            series: espCompletionData.InstalledPump2.Series,
          }),
        );
      }
    }
    if (espCompletionData?.InstalledPump3?.Manufacturer) {
      dispatch(
        fetchPumpManufacturerSeries({
          field: 'pumpLowerManufacturer',
          manufacturer: espCompletionData.InstalledPump3.Manufacturer,
        }),
      );
      if (espCompletionData?.InstalledPump3?.Series) {
        dispatch(
          fetchPumpManufacturerTypes({
            field: 'pumpLowerSeries',
            manufacturer: espCompletionData.InstalledPump3.Manufacturer,
            series: espCompletionData.InstalledPump3.Series,
          }),
        );
      }
    }
  }, [espCompletionData]);

  const createApiPayload = useCallback(() => {
    const methodOfProduction = parseInt(determineLiftType);
    const currentBoresData = boresTabRef.current?.getCommittedData() || [];
    const rawValvesData = valvesTabRef.current?.getCommittedData() || [];

    const currentValvesData = rawValvesData.map((valve, index) => {
      if (isEditMode && glValveCompletionData && index < glValveCompletionData.length) {
        return {
          ...fixValvePayload(valve, index),
          Id: glValveCompletionData[index].Id,
          Index: glValveCompletionData[index].Index,
        };
      } else {
        return fixValvePayload(valve, index);
      }
    });

    return {
      BlockUpdateOverride: installation.blockUpdates ?? false,
      DesignDate: null,
      ESPCompletion:
        methodOfProduction === 2
          ? {
            PumpMeasured: completionData.PumpMeasured ?? null,
            PumpTrueVertical: completionData.PumpTrueVertical ?? null,
            BottomholeMeasured: completionData.BottomholeMeasured ?? '',
            BottomholeTrueVertical: completionData.BottomholeTrueVertical ?? null,
            GeometryTubingOD: completionData.GeometryTubingOD ?? null,
            GeometryTubingID: completionData.GeometryTubingID ?? null,
            GeometryTubingRoughness: completionData.GeometryTubingRoughness ?? null,
            GeometryCasingOD: completionData.GeometryCasingOD ?? null,
            GeometryCasingID: completionData.GeometryCasingID ?? null,
            GeometryCasingRoughness: completionData.GeometryCasingRoughness ?? null,
            GeometryPumpIntakeRestriction: completionData.GeometryPumpIntakeRestriction ?? null,
            MotorOD: completionData.MotorOD ?? null,
            MotorHp60Hz: completionData.MotorHp60Hz ?? null,
            MotorVolts60Hz: completionData.MotorVolts60Hz ?? null,
            MotorCurrents60Hz: completionData.MotorCurrents60Hz ?? null,
            VsdNameplateAmps: completionData.VsdNameplateAmps ?? null,
            VsdNameplateKva: completionData.VsdNameplateKva ?? null,
            MotorToPumpSpeedRatio: completionData.MotorToPumpSpeedRatio ?? null,
            ShaftLimitHp60Hz: completionData.ShaftLimitHp60Hz ?? null,
            CableNo: completionData.CableNo ?? null,
            CableRoundFlat: completionData.CableRoundFlat?.toLocaleLowerCase() || null,

            InstalledPump1: {
              Id: isEditMode ? espCompletionData.InstalledPump1.Id : 0,
              Position: 1,
              Manufacturer: pumpData.pumpUpperManufacturer || '',
              Series: pumpData.pumpUpperSeries || '',
              Type: pumpData.pumpUpperType || '',
              PumpId: pumpData.pumpUpperId ?? 0,
              Stages: pumpData.pumpUpperTVD ?? null,
            },
            InstalledPump2: {
              Id: isEditMode ? espCompletionData.InstalledPump2.Id : 0,
              Position: 2,
              Manufacturer: pumpData.pumpMidManufacturer || '',
              Series: pumpData.pumpMidSeries || '',
              Type: pumpData.pumpMidType || '',
              PumpId: pumpData.pumpMidId ?? 0,
              Stages: pumpData.pumpMidTVD ?? null,
            },
            InstalledPump3: {
              Id: isEditMode ? espCompletionData.InstalledPump3.Id : 0,
              Position: 3,
              Manufacturer: pumpData.pumpLowerManufacturer || '',
              Series: pumpData.pumpLowerSeries || '',
              Type: pumpData.pumpLowerType || '',
              PumpId: pumpData.pumpLowerId ?? 0,
              Stages: pumpData.pumpLowerTVD ?? null,
            },

            GasSeparator: pumpData.gasSeparator ?? false,
            SeparationEfficiency: pumpData.gasSeparationEfficiency ?? 0,
            LiquidSeparationEfficiency: pumpData.liquidSeparationEfficiency ?? 0,
            PerMagneticMotor: pumpData.permanentMagnetMotor ?? false,
            SubCool: pumpData.subcool ?? false,
            Co2Flood: pumpData.co2Flood ?? false,
          }
          : null,
      FailDate: runlife.failDate ? formatDateWithUtcOffset(runlife.failDate, utcOffset) : null,
      FailureCause: null,
      Fluid:
        methodOfProduction === 2
          ? {
            Id: fluidCompletionsData.Id ?? 0,
            OilGravity: fluid.oilGravity ?? null,
            GasGravity: fluid.gasGravity ?? null,
            ReservoirPressureStatic: fluid.reservoirPressure ?? null,
            ReservoirTemperature: fluid.reservoirTemperature ?? null,
            ReservoirInflowModel: fluid.inflowModel ? parseInt(fluid.inflowModel) : null,
          }
          : null,
      GLCompletion: GL_TYPES.has(String(methodOfProduction))
        ? {
          ReservoirPressure: parseFloat(String(glCompletion.reservoirPressure)) || 0,
          ReservoirTemperature: parseFloat(String(glCompletion.reservoirTemperature)) || null,
          PerforationDepthTrueVertical: 0,
          GasGravity: parseFloat(String(glCompletion.gasGravity)) || null,
          GasGravityInjected: parseFloat(String(glCompletion.gasGravityInjected)) || 0,
          GaugeDepth: parseFloat(String(glCompletion.gaugeDepth)) || 0,
          OilGravity: parseFloat(String(glCompletion.oilGravity)) || 0,
          SolutionGasOilRatioCorrelation: glCompletion.solutionGasOilRatio
            ? parseInt(glCompletion.solutionGasOilRatio)
            : 0,
          FormationVolumeFactorCorrelation: glCompletion.formationVolumeFactorCorrelation
            ? parseInt(glCompletion.formationVolumeFactorCorrelation)
            : 0,
          ViscosityCorrelation: glCompletion.viscosityCorrelation ? parseInt(glCompletion.viscosityCorrelation) : 0,
          InflowModel: glCompletion.inflowModel ? parseInt(glCompletion.inflowModel) : 0,
          WellheadTemperature: null,
          Bores: currentBoresData,
          Valves: currentValvesData,
        }
        : null,
      Id: isEditMode ? combinedCompletionsData?.Id : 0,
      InstallDate: runlife.installDate ? formatDateWithUtcOffset(runlife.installDate, utcOffset) : null,
      InstallNumber: installation.installNumber || 0,
      IsExcluded: installation.excludeFromReports ?? false,
      IsPurchasedSystem: installation.purchasedSystem ?? false,
      IsUsedSystem: installation.usedSystem ?? false,
      ManufacturerControllerId: installation.manufacturerController
        ? parseInt(installation.manufacturerController)
        : null,
      MethodOfProduction: methodOfProduction,
      OHTCDefault: parseFloat(String(glCompletion.ohtc)) || 5,
      PullDate: runlife.pullDate ? formatDateWithUtcOffset(runlife.pullDate, utcOffset) : null,
      RunlifeDays: runlife.runlifeDays,
      StartDate: runlife.startDate ? formatDateWithUtcOffset(runlife.startDate, utcOffset) : null,
      Status: installation.status || '',
      TeardownDate: runlife.teardownDate ? formatDateWithUtcOffset(runlife.teardownDate, utcOffset) : null,
      WellId: wellId,
      ControllerCatalogueId: installation.manufacturerController || null,
    };
  }, [
    installation,
    runlife,
    fluid,
    glCompletion,
    determineLiftType,
    wellId,
    boresTabRef,
    valvesTabRef,
    isEditMode,
    combinedCompletionsData,
    glValveCompletionData,
    completionData,
    pumpData,
  ]);

  const handleSave = useCallback(async () => {
    const isValid = validateTabs();

    if (!isValid) {
      const firstErrorTab = Object.keys(tabErrors).find((tabId) => tabErrors[tabId].length > 0);
      if (firstErrorTab) setActiveTab(firstErrorTab);
      return;
    }

    setBoresData(boresTabRef.current?.getCommittedData() || []);
    setValvesData(valvesTabRef.current?.getCommittedData() || []);

    try {
      const payload = createApiPayload();
      const result = isEditMode
        ? await dispatch(editCombinedCompletion(payload))
        : await dispatch(createCombinedCompletion(payload));

      const actionType = isEditMode ? editCombinedCompletion : createCombinedCompletion;
      if (actionType.fulfilled.match(result)) {
        const successMessage = isEditMode
          ? 'Well completion has been updated successfully.'
          : 'Well completion has been created successfully.';
        showSuccessToast('Completion saved successfully', successMessage);
        onSave?.();
        onClose();
      } else {
        let errorMessage = '';

        const errorPayload = result?.payload as { [key: string]: string[] };

        if (errorPayload && typeof errorPayload === 'object') {
          const lcFirst = (str: string) => str.charAt(0).toLowerCase() + str.slice(1);

          Object.entries(errorPayload).forEach(([field, messages]) => {
            const normalizedField = lcFirst(field);
            const tab =
              Object.keys(TAB_VALIDATION_CONFIGS).find((tabName) =>
                Object.prototype.hasOwnProperty.call(TAB_VALIDATION_CONFIGS[tabName], normalizedField),
              ) || 'installation';

            mapApiErrorsToFieldValidation({ [normalizedField]: messages }, tab, setFieldValidationErrors);
          });
          const firstKey = Object.keys(errorPayload)[0];
          if (firstKey) {
            errorMessage = errorPayload[firstKey][0];
          }
        }

        if (!errorMessage) {
          errorMessage = isEditMode
            ? editCompletionError || 'Failed to update completion. Please try again.'
            : createCompletionError || 'Failed to save completion. Please try again.';
        }

        showErrorToast('Save Failed', errorMessage);
      }
    } catch (error) {
      console.error('Error saving completion:', error);
      showErrorToast('Save failed', 'An unexpected error occurred while saving the completion.');
    }
  }, [
    validateTabs,
    tabErrors,
    createApiPayload,
    dispatch,
    onSave,
    onClose,
    showSuccessToast,
    showErrorToast,
    createCompletionError,
    editCompletionError,
    isEditMode,
  ]);

  const updateFormData = useCallback(() => {
    let initialLiftType = '';
    if (
      combinedCompletionsData?.MethodOfProduction !== undefined &&
      combinedCompletionsData?.MethodOfProduction !== null
    ) {
      initialLiftType = String(combinedCompletionsData.MethodOfProduction);
    } else if (
      combinedCompletionsData?.LiftTypeValue !== undefined &&
      combinedCompletionsData?.LiftTypeValue !== null
    ) {
      initialLiftType = String(combinedCompletionsData.LiftTypeValue);
    } else if (selectedLiftType) {
      initialLiftType = selectedLiftType;
    } else {
      initialLiftType = initialInstallationState.liftType;
    }

    // Find manufacturer name by ControllerCatalogueId
    let manufacturerName = '';
    const controllerCatalogueId = combinedCompletionsData?.ControllerCatalogueId || '';
    if (manufacturersWithControllers && controllerCatalogueId) {
      const manufacturer = manufacturersWithControllers.find((m: any) =>
        m.Controllers?.some((c: any) => c.Id === controllerCatalogueId),
      );
      manufacturerName = manufacturer?.Name || '';
    }

    setInstallation({
      ...initialInstallationState,
      liftType: initialLiftType,
      manufacturer: manufacturerName,
      manufacturerController: controllerCatalogueId,
      installNumber: combinedCompletionsData?.InstallNumber || null,
      status: combinedCompletionsData?.Status || '',
      blockUpdates: combinedCompletionsData?.BlockUpdateOverride || false,
      excludeFromReports: combinedCompletionsData?.IsExcluded || false,
      purchasedSystem: combinedCompletionsData?.IsPurchasedSystem || false,
      usedSystem: combinedCompletionsData?.IsUsedSystem || false,
    });

    setRunlife({
      ...initialRunlifeState,
      installDate: parseApiDate(combinedCompletionsData?.InstallDate),
      startDate: parseApiDate(combinedCompletionsData?.StartDate),
      failDate: parseApiDate(combinedCompletionsData?.FailDate),
      pullDate: parseApiDate(combinedCompletionsData?.PullDate),
      teardownDate: parseApiDate(combinedCompletionsData?.TeardownDate),
      runlifeDays: combinedCompletionsData?.RunlifeDays || 0,
    });

    setFluid({
      ...initialFluidState,
      oilGravity: fluidCompletionsData?.OilGravity || null,
      gasGravity: fluidCompletionsData?.GasGravity || null,
      reservoirPressure: fluidCompletionsData?.ReservoirPressureStatic || null,
      reservoirTemperature: fluidCompletionsData?.ReservoirTemperature || null,
      inflowModel:
        fluidCompletionsData?.ReservoirInflowModel !== undefined
          ? String(fluidCompletionsData.ReservoirInflowModel)
          : '',
    });
    setPumpData({
      ...initialPumpState,
      pumpUpperManufacturer: espCompletionData?.InstalledPump1?.Manufacturer || '',
      pumpUpperSeries: espCompletionData?.InstalledPump1?.Series || '',
      pumpUpperId: espCompletionData?.InstalledPump1?.Id || null,
      pumpUpperType: espCompletionData?.InstalledPump1?.Type || '',
      pumpUpperTVD: espCompletionData?.InstalledPump1?.Stages || null,
      pumpMidManufacturer: espCompletionData?.InstalledPump2?.Manufacturer || '',
      pumpMidSeries: espCompletionData?.InstalledPump2?.Series || '',
      pumpMidId: espCompletionData?.InstalledPump2?.Id || null,
      pumpMidType: espCompletionData?.InstalledPump2?.Type || '',
      pumpMidTVD: espCompletionData?.InstalledPump2?.Stages || null,
      pumpLowerManufacturer: espCompletionData?.InstalledPump3?.Manufacturer || '',
      pumpLowerSeries: espCompletionData?.InstalledPump3?.Series || '',
      pumpLowerId: espCompletionData?.InstalledPump3?.Id || null,
      pumpLowerType: espCompletionData?.InstalledPump3?.Type || '',
      pumpLowerTVD: espCompletionData?.InstalledPump3?.Stages || null,
      gasSeparationEfficiency: espCompletionData?.SeparationEfficiency || null,
      subcool: espCompletionData?.SubCool || null,
      permanentMagnetMotor: espCompletionData?.PerMagneticMotor || null,
      gasSeparator: espCompletionData?.GasSeparator || null,
      liquidSeparationEfficiency: espCompletionData?.LiquidSeparationEfficiency || null,
      co2Flood: espCompletionData?.Co2Flood || null,
    });

    setGLCompletion({
      ...initialGLCompletionState,
      reservoirPressure: glCompletionData?.ReservoirPressure || '',
      reservoirTemperature: glCompletionData?.ReservoirTemperature || '',
      gasGravity: glCompletionData?.GasGravity || '',
      gasGravityInjected: glCompletionData?.GasGravityInjected || '',
      oilGravity: glCompletionData?.OilGravity || '',
      solutionGasOilRatio:
        glCompletionData?.SolutionGasOilRatioCorrelation !== undefined
          ? String(glCompletionData.SolutionGasOilRatioCorrelation)
          : '',
      formationVolumeFactorCorrelation:
        glCompletionData?.FormationVolumeFactorCorrelation !== undefined
          ? String(glCompletionData.FormationVolumeFactorCorrelation)
          : '',
      viscosityCorrelation:
        glCompletionData?.ViscosityCorrelation !== undefined ? String(glCompletionData.ViscosityCorrelation) : '',
      inflowModel: glCompletionData?.InflowModel !== undefined ? String(glCompletionData.InflowModel) : '',
      ohtc: combinedCompletionsData?.OHTCDefault || null,
      gaugeDepth: glCompletionData?.GaugeDepth || null,
    });

    setCompletionData({
      ...initialCompletionState,
      OHTCDefault: combinedCompletionsData?.OHTCDefault || null,
      PumpMeasured: combinedCompletionsData?.ESPCompletion?.PumpMeasured || null,
      PumpTrueVertical: combinedCompletionsData?.ESPCompletion?.PumpTrueVertical || null,
      BottomholeMeasured: combinedCompletionsData?.ESPCompletion?.BottomholeMeasured || null,
      BottomholeTrueVertical: combinedCompletionsData?.ESPCompletion?.BottomholeTrueVertical || null,
      GeometryTubingOD: combinedCompletionsData?.ESPCompletion?.GeometryTubingOD || null,
      GeometryTubingID: combinedCompletionsData?.ESPCompletion?.GeometryTubingID || null,
      GeometryTubingRoughness: combinedCompletionsData?.ESPCompletion?.GeometryTubingRoughness || null,
      GeometryCasingOD: combinedCompletionsData?.ESPCompletion?.GeometryCasingOD || null,
      GeometryCasingID: combinedCompletionsData?.ESPCompletion?.GeometryCasingID || null,
      GeometryCasingRoughness: combinedCompletionsData?.ESPCompletion?.GeometryCasingRoughness || null,
      MotorOD: combinedCompletionsData?.ESPCompletion?.MotorOD || null,
      MotorHp60Hz: combinedCompletionsData?.ESPCompletion?.MotorHp60Hz || null,
      MotorVolts60Hz: combinedCompletionsData?.ESPCompletion?.MotorVolts60Hz || null,
      MotorCurrents60Hz: combinedCompletionsData?.ESPCompletion?.MotorCurrents60Hz || null,
      VsdNameplateAmps: combinedCompletionsData?.ESPCompletion?.VsdNameplateAmps || null,
      VsdNameplateKva: combinedCompletionsData?.ESPCompletion?.VsdNameplateKva || null,
      MotorToPumpSpeedRatio: combinedCompletionsData?.ESPCompletion?.MotorToPumpSpeedRatio || null,
      GeometryPumpIntakeRestriction: combinedCompletionsData?.ESPCompletion?.GeometryPumpIntakeRestriction || null,
      ShaftLimitHp60Hz: combinedCompletionsData?.ESPCompletion?.ShaftLimitHp60Hz || null,
      CableNo: combinedCompletionsData?.ESPCompletion?.CableNo || null,
      CableRoundFlat: combinedCompletionsData?.ESPCompletion?.CableRoundFlat || null,
    });
  }, [
    determineLiftType,
    fluidCompletionsData,
    glCompletionData,
    combinedCompletionsData,
    manufacturersWithControllers,
  ]);
  const updateValidationError = (tabname: string, errors: { [fieldName: string]: string }) => {
    setFieldValidationErrors((prev) => ({ ...prev, [tabname]: errors }));
    setTabErrors((prev) => ({ ...prev, [tabname]: Object.values(errors).filter(Boolean) }));
  };
  const prevCompletionsDataRef = useRef<any>(null);
  useEffect(() => {
    if (isOpen) {
      const completionsChanged = prevCompletionsDataRef.current !== combinedCompletionsData;
      if (completionsChanged) {
        setActiveTab('installation');
        updateFormData();
        setTabErrors({});
        setFieldValidationErrors({});
        setBoresData(glBoreCompletionData || []);
        setValvesData(glValveCompletionData || []);
        prevCompletionsDataRef.current = combinedCompletionsData;
      }
    } else {
      prevCompletionsDataRef.current = null;
    }
  }, [isOpen, combinedCompletionsData, updateFormData, glBoreCompletionData, glValveCompletionData]);

  const renderTabContent = useCallback(() => {
    const tabComponents = {
      installation: (
        <CompletionsInstallationTab
          ref={installationTabRef}
          installation={installation}
          setInstallation={setInstallation}
          externalValidationErrors={fieldValidationErrors.installation || {}}
          isEditMode={isEditMode}
        />
      ),
      runlife: (
        <CompletionsRunlifeTab
          ref={runlifeTabRef}
          runlife={runlife}
          setRunlife={setRunlife}
          externalValidationErrors={fieldValidationErrors.runlife || {}}
        />
      ),
      completion: GL_TYPES.has(determineLiftType) ? (
        <CompletionsGLCompletionsTab
          ref={glCompletionTabRef}
          glCompletion={glCompletion}
          setGLCompletion={setGLCompletion}
          externalValidationErrors={fieldValidationErrors.glCompletion || {}}
        />
      ) : (
        <CompletionsCompletionTab
          ref={completionTabRef}
          completion={completionData}
          setCompletion={setCompletionData}
          externalValidationErrors={fieldValidationErrors.completion || {}}
          updateValidationError={updateValidationError}
        />
      ),
      pumps: (
        <CompletionsPumpsTab
          ref={pumpTabRef}
          pumpData={pumpData}
          setPumpData={setPumpData}
          externalValidationErrors={fieldValidationErrors.pumps || {}}
          updateValidationError={updateValidationError}
        />
      ),
      fluid: (
        <CompletionsFluidTab
          ref={fluidTabRef}
          fluid={fluid}
          setFluid={setFluid}
          externalValidationErrors={fieldValidationErrors.fluid || {}}
          updateValidationError={updateValidationError}
        />
      ),
      bores: <CompletionsBoresTab ref={boresTabRef} data={boresData} />,
      valves: <CompletionsValvesTab ref={valvesTabRef} data={valvesData} />,
    };

    return (
      <>
        {activeTab === 'installation' && tabComponents.installation}
        {activeTab === 'runlife' && tabComponents.runlife}
        {activeTab === 'completion' && tabComponents.completion}
        {activeTab === 'pumps' && tabComponents.pumps}
        {activeTab === 'fluid' && tabComponents.fluid}
        <div style={{ display: activeTab === 'bores' ? 'block' : 'none', width: '100%', height: '100%' }}>
          {tabComponents.bores}
        </div>
        <div style={{ display: activeTab === 'valves' ? 'block' : 'none', width: '100%', height: '100%' }}>
          {tabComponents.valves}
        </div>
      </>
    );
  }, [
    installation,
    runlife,
    fluid,
    pumpData,
    glCompletion,
    determineLiftType,
    fieldValidationErrors,
    glBoreCompletionData,
    glValveCompletionData,
    activeTab,
  ]);

  const handleTabChange = (nextTab: string) => {
    if (activeTab === 'valves') {
      setValvesData(valvesTabRef.current?.getCommittedData() || []);
      valvesTabRef.current?.discardUnsaved?.();
    }
    if (activeTab === 'bores') {
      setBoresData(boresTabRef.current?.getCommittedData() || []);
      boresTabRef.current?.discardUnsaved?.();
    }
    setActiveTab(nextTab);
  };

  return (
    <EntityModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      onSave={handleSave}
      enhancedTabs={enhancedTabs}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      isSubmitting={createCompletionLoading}
    >
      {combinedCompletionsLoading || createCompletionLoading ? (
        <div className='completion-modal-loading' style={{ padding: '40px', textAlign: 'center' }}>
          <Loader />
        </div>
      ) : (
        renderTabContent()
      )}
    </EntityModal>
  );
};

export default CreateCompletionsModal;
